import { getCountries, type Country } from 'react-phone-number-input';

export type LanguageItem = {
  locale: string;
  label: string;
  code: string;
  flagFile: string;
};

// Order is the "All languages" order from the design: A–Z by romanised name, CJK last.
export const LANGUAGE_ITEMS: readonly LanguageItem[] = [
  { locale: 'ar', label: 'العربية', code: 'AR', flagFile: 'ar.png' },
  { locale: 'id', label: 'Bahasa Indonesia', code: 'ID', flagFile: 'id.svg' },
  { locale: 'ms', label: 'Bahasa Melayu', code: 'MS', flagFile: 'ms.png' },
  { locale: 'bg', label: 'Български', code: 'BG', flagFile: 'bg.svg' },
  { locale: 'cs', label: 'Čeština', code: 'CS', flagFile: 'cs.png' },
  { locale: 'da', label: 'Dansk', code: 'DA', flagFile: 'da.png' },
  { locale: 'de', label: 'Deutsch', code: 'DE', flagFile: 'de.png' },
  { locale: 'et', label: 'Eesti', code: 'ET', flagFile: 'et.svg' },
  { locale: 'el', label: 'Ελληνικά', code: 'EL', flagFile: 'el.svg' },
  { locale: 'en-uk', label: 'English (UK)', code: 'EN', flagFile: 'uk.png' },
  { locale: 'en-us', label: 'English (US)', code: 'EN', flagFile: 'us.png' },
  { locale: 'es', label: 'Español', code: 'ES', flagFile: 'es.png' },
  { locale: 'fil', label: 'Filipino', code: 'FIL', flagFile: 'fil.svg' },
  { locale: 'fr', label: 'Français', code: 'FR', flagFile: 'fr.png' },
  { locale: 'hi', label: 'हिन्दी', code: 'HI', flagFile: 'hi.svg' },
  { locale: 'hr', label: 'Hrvatski', code: 'HR', flagFile: 'hr.svg' },
  { locale: 'zu', label: 'isiZulu', code: 'ZU', flagFile: 'zu.svg' },
  { locale: 'it', label: 'Italiano', code: 'IT', flagFile: 'it.png' },
  { locale: 'he', label: 'עברית', code: 'HE', flagFile: 'he.png' },
  { locale: 'ka', label: 'ქართული', code: 'KA', flagFile: 'ka.svg' },
  { locale: 'lv', label: 'Latviešu', code: 'LV', flagFile: 'lv.svg' },
  { locale: 'lt', label: 'Lietuvių', code: 'LT', flagFile: 'lt.svg' },
  { locale: 'hu', label: 'Magyar', code: 'HU', flagFile: 'hu.svg' },
  { locale: 'nl', label: 'Nederlands', code: 'NL', flagFile: 'nl.svg' },
  { locale: 'no', label: 'Norsk', code: 'NO', flagFile: 'no.png' },
  { locale: 'pl', label: 'Polski', code: 'PL', flagFile: 'pl.svg' },
  { locale: 'pt-PT', label: 'Português', code: 'PT', flagFile: 'pt-pt.png' },
  { locale: 'pt-BR', label: 'Português (Brasil)', code: 'BR', flagFile: 'pt-br.png' },
  { locale: 'ru', label: 'Русский', code: 'RU', flagFile: 'ru.png' },
  { locale: 'sq', label: 'Shqip', code: 'SQ', flagFile: 'sq.svg' },
  { locale: 'sk', label: 'Slovenčina', code: 'SK', flagFile: 'sk.svg' },
  { locale: 'fi', label: 'Suomi', code: 'FI', flagFile: 'fi.svg' },
  { locale: 'sv', label: 'Svenska', code: 'SE', flagFile: 'sw.png' },
  { locale: 'th', label: 'ไทย', code: 'TH', flagFile: 'th.svg' },
  { locale: 'vi', label: 'Tiếng Việt', code: 'VI', flagFile: 'vi.svg' },
  { locale: 'tr', label: 'Türkçe', code: 'TR', flagFile: 'tr.svg' },
  { locale: 'zh-CN', label: '简体中文', code: 'CN', flagFile: 'zh-cn.png' },
  { locale: 'zh-TW', label: '繁體中文', code: 'TW', flagFile: 'zh-tw.png' },
  { locale: 'ja', label: '日本語', code: 'JA', flagFile: 'ja.png' },
  { locale: 'ko', label: '한국어', code: 'KO', flagFile: 'ko.png' },
];

const SUGGESTED_FALLBACK: readonly string[] = ['en-us', 'es', 'ja', 'pt-BR', 'zh-TW'];

export const flagUrl = (flagFile: string) => `/assets/images/welcome/flags/${flagFile}`;

const byLocale = (locale?: string | null) =>
  LANGUAGE_ITEMS.find((i) => i.locale === locale) ?? null;

const isEnglish = (item: LanguageItem | null): item is LanguageItem =>
  !!item && item.locale.startsWith('en-');

export function findLanguageItem(tag?: string | null): LanguageItem | null {
  if (!tag) return null;

  try {
    const { language, script, region } = new Intl.Locale(tag.replace('_', '-')).maximize();

    if (language === 'en') return byLocale(region === 'GB' ? 'en-uk' : 'en-us');
    if (language === 'zh') return byLocale(script === 'Hant' ? 'zh-TW' : 'zh-CN');
    if (language === 'pt') return byLocale(region === 'BR' ? 'pt-BR' : 'pt-PT');
    if (language === 'nb' || language === 'nn') return byLocale('no');

    return byLocale(language);
  } catch {
    return null;
  }
}

// The row for the language the page is shown in. Both English rows share one translation,
// so the saved pick, then the browser, decide which of them is current.
export function getCurrentLanguageItem(
  i18nLang?: string,
  savedLocale?: string | null,
  browserLang?: string,
): LanguageItem {
  const current = findLanguageItem(i18nLang);
  if (current && !isEnglish(current)) return current;

  const saved =
    byLocale(savedLocale) ??
    (/^en-/i.test(savedLocale ?? '') ? findLanguageItem(savedLocale) : null);
  if (isEnglish(saved)) return saved;

  const browser = findLanguageItem(browserLang);
  return isEnglish(browser) ? browser : byLocale('en-us')!;
}

export function getSuggestedLanguageItems(
  browserLang?: string | null,
  countryByIp?: string | null,
): LanguageItem[] {
  const browserItem = findLanguageItem(browserLang);
  const ipItem = countryByIp ? findLanguageItem(`und-${countryByIp}`) : null;
  // Browser and IP give the same language: it takes a single row, the defaults keep all of theirs.
  const limit = browserItem && browserItem === ipItem ? 6 : 5;
  const unique = new Set(
    [browserItem, ipItem, ...SUGGESTED_FALLBACK.map(byLocale)].filter(
      (item): item is LanguageItem => !!item,
    ),
  );

  return Array.from(unique).slice(0, limit);
}

export const getAllLanguageItems = (suggested: readonly LanguageItem[]): LanguageItem[] =>
  LANGUAGE_ITEMS.filter((item) => !suggested.includes(item));

const LANGUAGE_CODES = ['en', 'en-GB', ...LANGUAGE_ITEMS.map((item) => item.locale)];

const resolveLanguageCode = (code = '') =>
  LANGUAGE_CODES.find((locale) => locale.toLowerCase() === code.toLowerCase()) || null;

const resolveCountryCode = (code = '') => {
  const COUNTRY_CODES = getCountries();

  return COUNTRY_CODES.includes(code.toUpperCase() as Country) ? code.toUpperCase() : null;
};

const parsePathPrefix = (first: string, second: string) => {
  const countryLanguage = resolveLanguageCode(second);
  const linkCountry = countryLanguage && resolveCountryCode(first);

  if (linkCountry) {
    return { language: countryLanguage, linkCountry, prefix: `/${first}/${second}` };
  }

  const language = resolveLanguageCode(first);
  return { language, linkCountry: null, prefix: language ? `/${first}` : '' };
};

export function parseOnboardingPath(pathname: string) {
  const [, first = '', second = ''] = pathname.split('/');
  const pathPrefix = parsePathPrefix(first, second);

  return {
    ...pathPrefix,
    cleanPath: pathname.slice(pathPrefix.prefix.length) || '/',
  };
}

export const buildOnboardingPath = (
  language: string,
  linkCountry: string | null,
  cleanPath: string,
) => `${linkCountry ? `/${linkCountry.toLowerCase()}` : ''}/${language}${cleanPath}`;

const LANGUAGE_PARAM = 'lang';

export const getSearchLanguage = (search: string) =>
  resolveLanguageCode(new URLSearchParams(search).get(LANGUAGE_PARAM) ?? '');

// changes only the `lang` value; other params are kept as typed (URLSearchParams would re-encode them)
const setSearchLanguage = (search: string, language: string) => {
  const params = search
    .replace('?', '')
    .split('&')
    .map((param) =>
      param.split('=')[0] === LANGUAGE_PARAM
        ? `${LANGUAGE_PARAM}=${encodeURIComponent(language)}`
        : param,
    );

  return `?${params.join('&')}`;
};

// the url after a language switch: the language is changed where the link has it (path or `lang` param);
// null when there is nothing to change, e.g. an old link without a language
export const getLanguageSwitchUrl = (
  pathname: string,
  search: string,
  hash: string,
  language: string,
) => {
  const { prefix, linkCountry, cleanPath } = parseOnboardingPath(pathname);
  const currentUrl = `${pathname}${search}${hash}`;
  let nextUrl = currentUrl;

  if (prefix) {
    nextUrl = `${buildOnboardingPath(language, linkCountry, cleanPath)}${search}${hash}`;
  } else if (getSearchLanguage(search)) {
    nextUrl = `${pathname}${setSearchLanguage(search, language)}${hash}`;
  }

  return nextUrl === currentUrl ? null : nextUrl;
};
